/**
 * LeadTimeCalculator Web Component
 * Rational Futurism Design
 */

// [2026-06] 加工系 form_type 一覧 (商品ページとカート集計で 14 営業日固定)
// [2026-06-19] frame_only(額縁単品=木製/アルミフレーム・加工なし)は除外。額縁単品は
// isSpecial5To7Days 経由で 7 営業日に統一する (石黒 QA / ⑥)。除外しないとカート集計で 14 にされる。
const PROCESSING_FORM_TYPES = new Set([
    'styrene_panel', 'wood_panel', 'aluminum_overmat',
    'aluminum_book_mat', 'aluminum_frame', 'wood_frame', 'wood_book_mat',
    'wood_overmat', 'canvas_panel', 'book_matte', 'wood_unified', 'aluminum_unified',
]);

// [2026-06] 用紙印刷の納期を Google Sheets「納期一覧」早見表と整数演算で完全一致させる。
//
// Sheets の真の計算式: 必要日数 = round(Σ(枚数_i / ポイントR_i))
//   - ポイントR = 1 桁小数 (L=4.6, A4=4.2, A2=2.2, ..., 1000×3000=0.7)
//   - 単純化ポイントS = 4.6/R (variant の delivery_rule_ref.point_value に 2 桁丸めで格納)
//   - 早見表閾値: N営業日まで = CEILING(R×(N+0.5))-1
//
// variant の格納値 S(2桁) から ri = round(46/S) で R×10 の整数を完全復元できる (全45サイズ検証済)。
// 浮動小数を避け、Σ(10·枚数/ri) を有理数(整数)で厳密合算 → round half up。
// これにより .5 ちょうどの境界 (例 A2 8枚, F10 21枚) も早見表と完全一致する。
function _ltGcd(a, b) {
    a = Math.abs(a); b = Math.abs(b);
    while (b) { const t = a % b; a = b; b = t; }
    return a || 1;
}
// lines: [{qty, points}]  points = 単純化ポイントS × 枚数 (Liquid 計算済み line_total_points)
// 戻り値: { days, raw } days=営業日(min3), raw=必要日数(小数, 要相談判定用)。有効行なし→null
function computePaperLeadDays(lines) {
    let num = 0, den = 1; // 必要日数 = num/den を厳密保持 (den = 出現 ri の LCM)
    let hasValid = false;
    for (const ln of lines) {
        const qty = ln.qty | 0;
        const points = ln.points;
        if (qty <= 0 || !points || points <= 0) continue;
        // 単位ポイント S = points/qty → ポイントR×10 整数 ri = round(46/S) = round(46·qty/points)
        const ri = Math.round(46 * qty / points);
        if (ri <= 0) continue;
        // num/den += (10·qty)/ri  (den を ri との LCM へ拡張)
        const g = _ltGcd(den, ri);
        const newDen = den / g * ri;
        num = num * (newDen / den) + (10 * qty) * (newDen / ri);
        den = newDen;
        hasValid = true;
    }
    if (!hasValid) return null;
    const raw = num / den;
    const days = Math.max(3, Math.floor((2 * num + den) / (2 * den))); // round half up
    return { days, raw };
}

class LeadTimeCalculator extends HTMLElement {
    constructor() {
        super();
        this._variantMap = {};
        this._dailyCapacity = 10;
        this._processingPoint = 0;
        this._tags = [];
        this._holidays = [];
        this._gwStart = null;
        this._gwEnd = null;
        this._gwExtraDays = 0;
        
        // Tier Config
        this._limitT1 = 40;
        this._capT1 = 10;
        this._capT2 = 20;
        this._consultLimit = 220;
    }

    _parseJSTDate(dateStr, isEnd) {
        if (!dateStr) return null;
        let s = dateStr.trim().replace(/\//g, '-');
        const hasTime = s.includes(' ') || s.includes('T');
        if (!hasTime) {
            s += isEnd ? 'T23:59:59+09:00' : 'T00:00:00+09:00';
        } else {
            s = s.replace(' ', 'T');
            if (!s.includes('+') && !s.includes('Z')) {
                s += '+09:00';
            }
        }
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d;
    }

    connectedCallback() {
        // Read data attributes
        try {
            this._variantMap = JSON.parse(this.dataset.pointsMap || '{}');
            // [2026-06] サイズ名 → points マップ (サイズが property の商品で [name="id"] 固定問題を回避)
            this._sizePointsMap = JSON.parse(this.dataset.sizePointsMap || '{}');
            this._tags = JSON.parse(this.dataset.tags || '[]');

            // Tier parsing
            this._limitT1 = parseInt(this.dataset.limitT1 || '40', 10);
            this._capT1 = parseInt(this.dataset.capT1 || '10', 10);
            this._capT2 = parseInt(this.dataset.capT2 || '20', 10);
            this._consultLimit = parseInt(this.dataset.consultLimit || '220', 10);

            // Holidays parsing
            if (this.dataset.holidays) {
                this._holidays = this.dataset.holidays.split(/[\n,]+/).map(d => d.trim()).filter(d => d);
            }

            // GW Config
            if (this.dataset.gwStart) this._gwStart = this._parseJSTDate(this.dataset.gwStart, false);
            if (this.dataset.gwEnd) this._gwEnd = this._parseJSTDate(this.dataset.gwEnd, true);
            
            const globalExtra = parseInt(this.dataset.gwExtraDays || '0', 10);
            const overrideStr = this.dataset.gwExtraOverride;
            if (overrideStr && overrideStr.trim() !== '') {
                this._gwExtraDays = parseInt(overrideStr, 10);
            } else {
                this._gwExtraDays = globalExtra;
            }

            // Fallback daily capacity (legacy)
            this._dailyCapacity = parseInt(this.dataset.capacity || '10', 10);
        } catch (e) {
            console.error('LeadTimeCalculator: Invalid JSON in data attributes', e);
        }

        this._processingPoint = parseFloat(this.dataset.processingPoint || '0');

        // Init Elements
        this.els = {
            days: this.querySelector('[data-target="days"]'),
            date: this.querySelector('[data-target="date"]'),
            prefix: this.querySelector('#rf-shipping-prefix'),
            // RF Delivery Estimation Elements
            rfCountdownText: this.querySelector('#rf-countdown-text'),
            rfDateReception: this.querySelector('#rf-date-reception'),
            rfDateShipping: this.querySelector('#rf-date-shipping'),
            rfDateDelivery: this.querySelector('#rf-date-delivery'),
            rfStepShipping: this.querySelector('#rf-step-shipping'),
            rfStepDelivery: this.querySelector('#rf-step-delivery'),
            rfTextSettings: this.querySelector('#rf-delivery-text-settings')
        };

        // Settings parsing from hidden block
        this._rfSettings = {
            timerPrefix: 'あと',
            timerSuffix: '以内のご注文で「最短発送」'
        };
        if (this.els.rfTextSettings) {
            const tempPrefix = this.els.rfTextSettings.querySelector('[data-timer-prefix]');
            const tempSuffix = this.els.rfTextSettings.querySelector('[data-timer-suffix]');
            if (tempPrefix) this._rfSettings.timerPrefix = tempPrefix.innerText;
            if (tempSuffix) this._rfSettings.timerSuffix = tempSuffix.innerText;
        }

        // [NEW] Hybrid mode: load initial cart items for cross-product aggregation
        this._cartAggregate = this.dataset.cartAggregate === 'true';
        this._initialCartItems = [];
        if (this._cartAggregate) {
            try {
                const scriptEl = document.getElementById('rf-initial-cart-items');
                if (scriptEl && scriptEl.textContent.trim()) {
                    const parsed = JSON.parse(scriptEl.textContent);
                    if (Array.isArray(parsed)) this._initialCartItems = parsed;
                }
            } catch (e) {
                console.warn('LeadTime: failed to parse initial cart items', e);
            }
        }

        // Listeners
        this.initListeners();

        // Initial Calc
        if (this.dataset.overridePoints) {
            // Cart Mode or Static Mode
            const points = parseFloat(this.dataset.overridePoints);
            this.updateUI(points);

            // [2026-06] ドロワー/カート画面の集計表示もバンドル削除時にリアルタイム更新する。
            // バンドル削除は cart:change/refresh を発火しない (テーマ保護) ため専用イベントを購読し、
            // /cart.js を再取得して data-cart-items を作り直して updateUI を再実行する。
            if (this.dataset.cartItems) {
                try {
                    this._cartItemsSnapshot = JSON.parse(this.dataset.cartItems);
                } catch (e) {
                    this._cartItemsSnapshot = [];
                }
                document.addEventListener('lead-time:cart-refresh', () => this._refreshDrawer());
            }
        } else {
            // Product Page Mode (with optional cart aggregation)
            this.handleProductPageUpdate();
        }
    }

    // [2026-06] ドロワー/カート画面の集計表示をリアルタイム更新 (バンドル削除/数量変更時)。
    // 初期 data-cart-items スナップショットから per-unit ポイントを引き継ぎ、/cart.js の最新
    // 数量・行構成に合わせて作り直す (小数 S を保持、Math.round しない)。
    async _refreshDrawer() {
        try {
            const res = await fetch('/cart.js', { credentials: 'same-origin' });
            if (!res.ok) return;
            const cart = await res.json();

            const byKey = new Map();
            // [2026-06-19] variantId 単独フォールバック。properties の直列化差で exact key が外れると
            // 既知 variant が「不明=14日」に誤判定され、ドロワー納期が 14↔正値で点滅する (③)。
            const byVariant = new Map();
            for (const it of (this._cartItemsSnapshot || [])) {
                const key = String(it.variantId || '') + '|' + JSON.stringify(it.properties || {});
                byKey.set(key, it);
                if (it.variantId != null && !byVariant.has(String(it.variantId))) {
                    byVariant.set(String(it.variantId), it);
                }
            }

            const rebuilt = [];
            let totalPoints = 0;
            for (const li of (cart.items || [])) {
                const variantIdStr = String(li.variant_id || li.id || '');
                const props = li.properties || {};
                const key = variantIdStr + '|' + JSON.stringify(props);
                const initial = byKey.get(key) || byVariant.get(variantIdStr);
                if (initial) {
                    const perUnit = initial.quantity > 0 ? (initial.points / initial.quantity) : initial.points;
                    const pts = perUnit * li.quantity;
                    rebuilt.push({ ...initial, quantity: li.quantity, points: pts });
                    totalPoints += pts;
                } else {
                    // スナップショット外 (別タブ追加等) は負荷不明として 14日フォールバック対象
                    rebuilt.push({
                        formType: '', title: li.product_title || '',
                        productId: String(li.product_id || ''), variantId: variantIdStr,
                        quantity: li.quantity, points: 0, hasDeliveryRule: false, properties: props
                    });
                }
            }

            this.dataset.cartItems = JSON.stringify(rebuilt);
            this.dataset.overridePoints = String(totalPoints);
            this.updateUI(totalPoints);
        } catch (e) {
            console.warn('LeadTime: drawer refresh failed', e);
        }
    }

    initListeners() {
        // If we have override points, we might not need to listen to form changes unless we want to handle cart updates.
        // But for cart drawer, the section rerenders on update, so connectedCallback runs again.
        if (this.dataset.overridePoints) return;

        // Product Page Listeners
        const handleChange = (e) => {
            if (e.target.matches('[name="id"], [name="quantity"], #rf-size-select, select[name="properties[サイズ]"], input[name="properties[種類]"], input[name="properties[レイアウト確認]"], input[name="properties[出力データ作成オプション]"]')) {
                this.handleProductPageUpdate();
            }
        };

        document.addEventListener('change', handleChange);
        document.addEventListener('input', handleChange);

        // Listen for clicks on quantity buttons (delegate)
        document.addEventListener('click', (e) => {
            if (e.target.closest('.quantity-selector__button') || e.target.closest('.rf-qty-btn')) {
                // Wait for the input value to update (short delay)
                setTimeout(() => this.handleProductPageUpdate(), 50);
            }
        });

        // Or custom events from main-product.js if it fires them
        document.addEventListener('variant:changed', () => this.handleProductPageUpdate());

        // Cart Context (if integrated in cart)
        // If re-rendered via Section Rendering API, connectedCallback runs again.
        // If we want to listen to cart events for dynamic updates without re-render:
        document.addEventListener('cart:updated', () => {
            if (this._cartAggregate) this._refreshCartItems();
        });
        // Also listen for the Shopify-native cart change event
        document.addEventListener('cart:change', () => {
            if (this._cartAggregate) this._refreshCartItems();
        });
        // [2026-06] バンドル削除 (Undo バッファ式) は cart:change/refresh を発火しない設計のため、
        // bundle-cart.js が専用イベントを送る。商品ページ納期を即時再集計する。
        document.addEventListener('lead-time:cart-refresh', () => {
            if (this._cartAggregate) this._refreshCartItems();
        });
    }

    async _refreshCartItems() {
        // Re-fetch /cart.js to get updated cart items, then recompute.
        // Note: /cart.js does not return metafields, so we approximate `points` from this._variantMap
        // when the line item's product matches this product page. For OTHER products, we cannot
        // recompute points without server-side help, so we keep their initial Liquid-rendered points
        // for items that still match the initial snapshot.
        try {
            const res = await fetch('/cart.js', { credentials: 'same-origin' });
            if (!res.ok) return;
            const cart = await res.json();
            const currentProductId = (this.dataset.productId || '').toString();
            const initialByKey = new Map();
            // [2026-06-19] variantId 単独フォールバック (③ 点滅対策・_refreshDrawer と同趣旨)。
            const initialByVariant = new Map();
            for (const it of this._initialCartItems) {
                const key = String(it.variantId || '') + '|' + JSON.stringify(it.properties || {});
                initialByKey.set(key, it);
                if (it.variantId != null && !initialByVariant.has(String(it.variantId))) {
                    initialByVariant.set(String(it.variantId), it);
                }
            }
            const rebuilt = [];
            for (const li of (cart.items || [])) {
                const variantIdStr = String(li.variant_id || li.id || '');
                const props = li.properties || {};
                const key = variantIdStr + '|' + JSON.stringify(props);
                const initial = initialByKey.get(key) || initialByVariant.get(variantIdStr);
                if (initial) {
                    // Reuse points from initial snapshot but update quantity.
                    // [2026-06] 小数の単純化ポイント S (例 A2=2.09) を保持するため Math.round しない。
                    // 丸めると ri 復元 (round(46·qty/points)) が狂い納期が早見表とズレる。
                    const perUnit = initial.quantity > 0 ? (initial.points / initial.quantity) : initial.points;
                    rebuilt.push({
                        ...initial,
                        quantity: li.quantity,
                        points: perUnit * li.quantity
                    });
                } else if (String(li.product_id || '') === currentProductId && this._variantMap[variantIdStr] != null) {
                    // New line item for current product: derive points from variant map
                    const vp = this._variantMap[variantIdStr] || 0;
                    rebuilt.push({
                        formType: (this.dataset.formType || '').trim(),
                        title: li.product_title || '',
                        productId: String(li.product_id || ''),
                        variantId: variantIdStr,
                        quantity: li.quantity,
                        points: (vp + this._processingPoint) * li.quantity,
                        hasDeliveryRule: vp > 0,
                        properties: props
                    });
                } else {
                    // Unknown product (added from another tab): use 0 points
                    // -> aggregation logic treats this as the "unconfigured" 14-day fallback
                    rebuilt.push({
                        formType: '',
                        title: li.product_title || '',
                        productId: String(li.product_id || ''),
                        variantId: variantIdStr,
                        quantity: li.quantity,
                        points: 0,
                        hasDeliveryRule: false,
                        properties: props
                    });
                }
            }
            this._initialCartItems = rebuilt;
            this.handleProductPageUpdate();
        } catch (e) {
            console.warn('LeadTime: cart refresh failed', e);
        }
    }

    isBusinessDay(date) {
        const day = date.getDay();
        if (day === 0) return false; // Sunday
        // If IS_COUNT_SATURDAY is needed, it would be passed via dataset. For now assume Saturday is off unless configured differently.
        if (day === 6) return false;

        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        const ds = `${y}-${m}-${d}`;

        if (this._holidays.includes(ds)) return false;

        return true;
    }

    addBusinessDays(startDate, daysToAdd) {
        // [FIX] User Request: Count reception date (startDate) as Day 1.
        // Therefore, if daysToAdd is 3, we only need to add 2 more business days.
        let count = 1;
        let current = new Date(startDate);

        while (count < daysToAdd) {
            current.setDate(current.getDate() + 1);
            if (this.isBusinessDay(current)) {
                count++;
            }
        }
        return current;
    }

    getFormattedDate(date) {
        const result = `${date.getMonth() + 1}/${date.getDate()}`;
        const days = ['日', '月', '火', '水', '木', '金', '土'];
        const dayStr = days[date.getDay()];
        return `${result} (${dayStr})`;
    }

    getReceptionDate() {
        const now = new Date();
        let baseDate = new Date(now);

        const CUTOFF_HOUR = 12;
        const CUTOFF_MINUTE = 0;

        // 2. Check Cutoff First
        const cutoff = new Date(now);
        cutoff.setHours(CUTOFF_HOUR, CUTOFF_MINUTE, 0, 0);

        if (now > cutoff) {
            // Next day
            baseDate.setDate(baseDate.getDate() + 1);
        }

        // 1. Is it a business day? If not, find the next business day
        while (!this.isBusinessDay(baseDate)) {
            baseDate.setDate(baseDate.getDate() + 1);
        }

        // Reset to start of day for clean calculation
        baseDate.setHours(0, 0, 0, 0);
        return baseDate;
    }

    updateCountdown() {
        if (!this.els.rfCountdownText) return;

        const CUTOFF_HOUR = 12;
        const CUTOFF_MINUTE = 0;

        const now = new Date();
        let targetCutoff = new Date(now);
        targetCutoff.setHours(CUTOFF_HOUR, CUTOFF_MINUTE, 0, 0);

        let isTodayTarget = false;

        if (this.isBusinessDay(now) && now < targetCutoff) {
            isTodayTarget = true;
        } else {
            targetCutoff.setDate(targetCutoff.getDate() + 1);
            while (!this.isBusinessDay(targetCutoff)) {
                targetCutoff.setDate(targetCutoff.getDate() + 1);
            }
            isTodayTarget = false;
        }

        const diff = targetCutoff - now;
        if (diff < 0) return;

        const h = Math.floor(diff / (1000 * 60 * 60));
        const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const s = Math.floor((diff % (1000 * 60)) / 1000);

        const hStr = String(h).padStart(2, '0');
        const mStr = String(m).padStart(2, '0');
        const sStr = String(s).padStart(2, '0');

        const dateStr = isTodayTarget ? "本日" : `${targetCutoff.getMonth() + 1}/${targetCutoff.getDate()}`;

        this.els.rfCountdownText.innerHTML =
            `${this._rfSettings.timerPrefix} <strong style="font-family:'JetBrains Mono'; letter-spacing:1px; margin: 0 0.4em;">${hStr}:${mStr}:${sStr}</strong> ${this._rfSettings.timerSuffix} <span style="border-bottom:1px solid currentColor; margin-left: 0.2em;">${dateStr}受付</span>`;
    }

    handleProductPageUpdate() {
        // 1. Get current variant ID
        const form = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
        if (!form) return;

        const variantInput = form.querySelector('[name="id"]');
        if (!variantInput) return;

        const qtyInput = form.querySelector('[name="quantity"]');
        const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
        const variantId = variantInput.value;

        // 2. Calculate Points
        // [2026-06] サイズが properties[サイズ] (variant ではなく property) の商品は
        // [name="id"] が基準 variant に固定され points がサイズ連動しない。
        // 選択中のサイズ名から size-points-map で直接引く方を優先する。
        let variantPoints = this._variantMap[variantId] || 0;
        if (this._sizePointsMap && Object.keys(this._sizePointsMap).length > 0) {
            // サイズセレクタはまず自フォーム内、無ければページ全体から探す
            // (product-info ブロックの lead-time-calculator は別フォームに居るため document 検索が必要)
            let sizeSel = form.querySelector('#rf-size-select, select[name="properties[サイズ]"]');
            if (!sizeSel) sizeSel = document.querySelector('#rf-size-select, select[name="properties[サイズ]"]');
            const sizeName = sizeSel ? (sizeSel.value || '').trim() : '';
            if (sizeName && this._sizePointsMap[sizeName] != null) {
                variantPoints = this._sizePointsMap[sizeName];
            }
        }

        // Total Points = (Variant Points * Qty) + Processing Points
        const totalPoints = (variantPoints + this._processingPoint) * qty;

        // [NEW] Hybrid mode: merge existing cart items + this product's pending item
        if (this._cartAggregate && Array.isArray(this._initialCartItems)) {
            const properties = this._extractFormProperties(form);
            const pendingItem = {
                formType: (this.dataset.formType || '').trim(),
                title: '__PENDING__',
                productId: this.dataset.productId || '',
                variantId: variantId,
                quantity: qty,
                points: (variantPoints + this._processingPoint) * qty,
                hasDeliveryRule: variantPoints > 0,
                properties: properties,
                _pending: true
            };

            // Filter out any prior pending entry, then append fresh one
            const baseItems = this._initialCartItems.filter(it => !it || it._pending !== true);
            const combined = baseItems.concat([pendingItem]);

            // Use cart-mode aggregation path
            this.dataset.cartItems = JSON.stringify(combined);
            this.updateUI(totalPoints);

            // [2026-06] カート最新化: AJAX 追加/削除の cart:change を取りこぼしても
            // 商品ページ集計が確実にカート内容を反映するよう、操作のたびに /cart.js を
            // debounce 再取得して _initialCartItems を作り直す (ループは _inCartRefresh で防ぐ)。
            if (!this._inCartRefresh) {
                clearTimeout(this._cartRefreshTimer);
                this._cartRefreshTimer = setTimeout(() => {
                    this._inCartRefresh = true;
                    Promise.resolve(this._refreshCartItems()).finally(() => {
                        this._inCartRefresh = false;
                    });
                }, 200);
            }
        } else {
            // Pure product-page mode (no cart aggregation)
            if (this.dataset.cartItems) delete this.dataset.cartItems;
            this.updateUI(totalPoints);
        }
    }

    _extractFormProperties(form) {
        // Collect all properties[*] inputs from the form into a plain object.
        // Mirrors the structure that cart.js / Liquid produce for line_item.properties.
        const properties = {};
        if (!form) return properties;
        const nodes = form.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]');
        nodes.forEach(node => {
            const m = node.name.match(/^properties\[(.+)\]$/);
            if (!m) return;
            const key = m[1];
            if (node.type === 'checkbox' || node.type === 'radio') {
                if (node.checked) properties[key] = node.value;
            } else if (node.value !== '') {
                properties[key] = node.value;
            }
        });
        return properties;
    }

    handleCartUpdate(cartData) {
        // TO BE IMPLEMENTED: Logic for Cart Drawer
        // Loop through items, find requiredDays for each, take MAX.
        // This is complex because we need points map for ALL items in cart.
        // For now, simpler implementation: 
        // We probably cannot calculate exact points for ALL items unless we have that data on the cart line item properties 
        // or a global JS map of all products (unlikely).
        // Strategy: We will render this component inside the cart drawer if possible with liquid data, 
        // OR fetch data. 
        // If this component is INSIDE a specific line item in the cart drawer, it behaves like product page (just that line).
        // If it is 'Cart Total Delivery', that's harder.

        // Scope of this task: "Cart Logic (Aggregation) ... max(All Items' requiredDays)"
        // This might require fetching checking cart items metaproperties or strictly relying on what we can render in Liquid.
        // I'll stick to Product Page logic primarily for now to ensure base functions.
    }

    generateMailto() {
        // 本文に出さない技術用 / Shopify 非表示キー
        const _skipKey = (k) => k.charAt(0) === '_' || /ファイルアップロード|管理用|ダウンロード|入稿データURL|URL/.test(k);

        // [2026-06-22] カート/ドロワ集計コンテキスト(class=lt-container)では closest('form') が
        // **カートフォーム**(商品 properties[] 入力を持たない)を返すため、従来のフォーム走査だと
        // オプションが空になる(Owner 報告: ドロワの問い合わせメールが「オプション:」空・数量1・全商品名のみ)。
        // → data-cart-items(JSON: title/variantTitle/quantity/properties) から全行を本文に組む。
        const _isCartCtx = (this.classList && typeof this.classList.contains === 'function' && this.classList.contains('lt-container'));
        if (_isCartCtx) {
            let items = Array.isArray(this._cartItemsSnapshot) ? this._cartItemsSnapshot : null;
            if (!items) { try { items = JSON.parse(this.dataset.cartItems || '[]'); } catch (e) { items = []; } }
            let lines = '';
            (items || []).forEach((it, idx) => {
                const t = it.title || '商品';
                const vt = (it.variantTitle && it.variantTitle !== 'Default Title') ? ` / ${it.variantTitle}` : '';
                lines += `${idx + 1}. ${t}${vt} × ${it.quantity == null ? 1 : it.quantity}\n`;
                const props = it.properties || {};
                Object.keys(props).forEach((k) => {
                    const v = (props[k] == null ? '' : String(props[k])).trim();
                    if (!v || _skipKey(k)) return;
                    lines += `   ${k}: ${v}\n`;
                });
            });
            if (!lines) lines = '(カート内容を取得できませんでした)\n';
            const subjectC = encodeURIComponent('【納期相談】カート内容について');
            const bodyC = encodeURIComponent(`以下のカート内容について納期の相談です。\n\n${lines}\n希望納期: \n備考: \n`);
            return `mailto:info@photopri.com?subject=${subjectC}&body=${bodyC}`;
        }

        // --- 商品ページ(PDP): 当該商品フォームから選択中オプションを収集 ---
        const productTitle = document.querySelector('h1')?.innerText || '商品';
        const qtyInput = document.querySelector('[name="quantity"]');
        const qty = qtyInput ? qtyInput.value : '不明';

        // Gather Options (Specific to custom-upload-form.liquid structure)
        // #custom-upload-form は実在しない id のため、updateUI と同じ堅牢版(cart/add フォーム)に頼る。
        const form = (typeof this.closest === 'function' ? this.closest('form') : null) || document.querySelector('form[action*="/cart/add"]');
        let optionsText = "";

        // [2026-06-23] 可視判定: 入力の祖先に display:none があれば除外(非表示セクション/サブグループの既定値を弾く)。
        // 入力自体はカスタムラジオで display:none(.rf-radio-pill input{display:none})なので親方向へ走査する。
        // ※ .rf-section 単位だと「表示中セクション内の非表示サブグループ」を取りこぼす:
        //   マットカラー #rf-mat-color-group は既定 display:none + 既定 checked=ホワイト。親セクション(color)が
        //   表示中だと旧 .rf-section 判定をすり抜け、未選択でも「マットカラー: ホワイト」が本文に漏れていた(Owner 報告)。
        const _ancestorHidden = (el) => {
            let node = el.parentElement;
            while (node && node !== form && node !== document.body) {
                const cs = (typeof getComputedStyle === 'function') ? getComputedStyle(node) : null;
                if (cs && cs.display === 'none') return true;
                node = node.parentElement;
            }
            return false;
        };

        if (form) {
            const inputs = form.querySelectorAll('input:checked, select');
            inputs.forEach(input => {
                // [2026-06-22] 顧客向け本文には注文 properties[...] のみ出す。rf-size-mode(定形/定形外)
                // ・rf-paper-size-mode 等の **技術用トグル(name が properties[ で始まらない)** は除外 (Owner 指摘)。
                if (!input.name || !input.name.startsWith('properties[')) return;
                if (_ancestorHidden(input)) return;   // 非表示セクション/サブグループの既定値(マット色等)を除外
                // select は value=handle(英語/コード)のことがあるため表示テキストを採用 (例: 用紙種類)。
                var val;
                if (input.tagName === 'SELECT' && input.selectedIndex >= 0 && input.options[input.selectedIndex]) {
                    val = (input.options[input.selectedIndex].textContent || input.value || '').trim();
                } else {
                    val = (input.value || '').trim();
                }
                if (!val) return;
                // properties[ラベル] → ラベル に整形。Shopify 非表示(_始まり)・技術用フィールドは本文に出さない。
                const label = input.name.replace(/^properties\[/, '').replace(/\]$/, '');
                if (label.charAt(0) === '_' || /ファイルアップロード|管理用|ダウンロード|入稿データURL|URL/.test(label)) return;
                optionsText += `${label}: ${val}\n`;
            });
        }

        const subject = encodeURIComponent(`【納期相談】${productTitle}について`);
        const body = encodeURIComponent(`以下の商品について納期の相談です。

商品名: ${productTitle}
数量: ${qty}
オプション:
${optionsText}

希望納期: 
備考: 
`);
        return `mailto:info@photopri.com?subject=${subject}&body=${body}`;
    }

    updateUI(totalPoints) {
        // Step 1: Calc Required Days
        let requiredDays;
        let isApprox = false;
        // [2026-06-22] カート/フォーム内の数量が 8 点以上なら納期確約不可 → オーバーフロー表示 (Owner)。
        // 額装加工系・額縁単品とも統一文言「ご注文後に納期をメールでお知らせ」+ 今すぐ問い合わせるボタン。
        let procOverflow = false;
        // 額縁単品(額単品)がカート集計で納期を駆動するとき「7営業日」表示にするフラグ。
        let cartHasSpecial5To7 = false;

        // [NEW] Feature: Check overrides based on Form Type or Zero Points
        const formType = (this.dataset.formType || '').trim();
        const productId = this.dataset.productId;
        // 額縁単品(frame_only): 2 商品の productId 固定 + formType でも判定 (新規額縁追加に追従)。
        const isSpecial5To7Days = (productId === '7207878197440' || productId === '7207808925888' || formType === 'frame_only');

        // [NEW] Hide lead time for Student Discount
        if (formType === 'student_discount') {
            const statusEl = this.querySelector('.rf-dashboard-status');
            if (statusEl) statusEl.style.display = 'none';
            return;
        }

        // Simple buy (paper sample books, etc.) — 一律 3 営業日。
        // points や size/qty 連動の escalation はスキップ。
        if (formType === 'simple_buy') {
            requiredDays = 3;
            isApprox = false;
        }
        // 1. Trial Print & Caption Panel (Fixed 5 days + caption +1, except specific variants)
        // [PRIORITY] formType 分岐は isSpecial5To7Days より先。caption_panel 商品の productId が
        // たまたま isSpecial5To7Days の固定 ID リストに含まれていても 7 日固定にハイジャックされないようにする。
        else if (formType === 'otameshi_print' || formType === 'caption_panel') {
            requiredDays = 5;
            // [NEW] Caption Panel: 「出力データ作成を希望する」選択時は +1 営業日
            if (formType === 'caption_panel') {
                const form0 = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
                const captionOptRadio = form0 ? form0.querySelector('input[name="properties[出力データ作成オプション]"]:checked') : null;
                if (captionOptRadio && captionOptRadio.value === '希望する') {
                    requiredDays += 1;
                }
            }
            // [FIX] Specific variant override: 全用紙セット → 7 business days
            // [NEW] Single-sheet variant override: シングル → 3 business days (like print_only)
            if (formType === 'otameshi_print') {
                let isAllPaperSet = false;
                let isSingleSheet = false;

                // Strategy 1: Check type radio (new 2-option products)
                const form = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
                // [2026-06-23] (G)対策: 種類radioが closest(form) から見えない配置でも document 横断で救済し、
                // シングル単品PDPが otameshi基準5日のまま固まる不具合を解消。fallback は isSingleSheet=true 方向のみで、
                // セット既定PDPには単品radioが checked で存在しないため誤検出しない(セット単独=5/7を維持)。
                let typeRadio = form ? form.querySelector('input[name="properties[種類]"]:checked') : null;
                if (!typeRadio) typeRadio = document.querySelector('input[name="properties[種類]"]:checked');
                if (typeRadio && /シングル|単品|1枚|single/i.test(typeRadio.value)) {
                    isSingleSheet = true;
                }

                // Strategy 2: Check #rf-size-select text for セット markers
                const sizeSelect = document.querySelector('#rf-size-select');
                if (sizeSelect && sizeSelect.selectedIndex >= 0) {
                    const selectedOpt = sizeSelect.options[sizeSelect.selectedIndex];
                    const selectedText = selectedOpt ? selectedOpt.textContent : '';
                    if (selectedText.includes('全用紙セット')) {
                        isAllPaperSet = true;
                    }
                    if (!isSingleSheet && /シングル|単品|1枚|single/i.test(selectedText)) {
                        isSingleSheet = true;
                    }
                }

                // Strategy 3: Fallback to variant ID for legacy 全用紙セット
                if (!isAllPaperSet) {
                    let variantInput = form ? form.querySelector('[name="id"]') : null;
                    if (!variantInput) {
                        variantInput = document.querySelector('select[name="id"], input[name="id"]');
                    }
                    const currentVariantId = variantInput ? variantInput.value : null;
                    if (currentVariantId === '41683292848320') {
                        isAllPaperSet = true;
                    }
                }

                if (isSingleSheet) {
                    // [2026-06] シングルは A5(=1.05) 紐付け済 → print_only と同じ Sheets 整数演算
                    const sumPoints = totalPoints || 0;
                    if (sumPoints <= 0) {
                        // [2026-06-19] ポイント未取得の初期描画で 14→3 の点滅が出ていた。
                        // シングルお試しは A5 で確定 3 営業日床のため、フォールバックも 3 に固定し点滅を解消。
                        requiredDays = 3;
                        isApprox = false;
                    } else {
                        const qtyEl = form ? form.querySelector('[name="quantity"]') : null;
                        const qtyForCalc = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;
                        const res = computePaperLeadDays([{ qty: qtyForCalc, points: sumPoints }]);
                        requiredDays = res ? res.days : 3;   // [2026-06-23] シングルは 14 でなく単品床 3 にフォールバック
                    }
                } else if (isAllPaperSet) {
                    // [2026-06-19] 全用紙セット 7 営業日に revert (石黒 QA。6月の 5 固定は誤り)
                    requiredDays = 7;
                }
            }
        }
        // [2026-06] 加工系 form_type は 14 営業日固定。[2026-06-19 v2] 8 点以上は お問い合わせ (Owner)。
        else if (PROCESSING_FORM_TYPES.has(formType)) {
            requiredDays = 14;
            isApprox = false;
            const _qf = (this.closest('form') || document.querySelector('form[action*="/cart/add"]'));
            const _q = _qf ? (parseInt((_qf.querySelector('[name="quantity"]') || {}).value, 10) || 1) : 1;
            if (_q >= 8) procOverflow = true;
        }
        // [2026-06-19 v2] 額縁単品(額単品): 1〜7点=5〜7営業日程度 / 8点以上=お問い合わせ (Owner)。
        // requiredDays は範囲 worst の 7。表示は下で「約5〜7営業日」に上書き。
        else if (isSpecial5To7Days) {
            requiredDays = 7;
            isApprox = true;
            const _qf = (this.closest('form') || document.querySelector('form[action*="/cart/add"]'));
            const _q = _qf ? (parseInt((_qf.querySelector('[name="quantity"]') || {}).value, 10) || 1) : 1;
            if (_q >= 8) procOverflow = true;
        }
        // [PRIORITY] Temporary Print Only / Photo Card Quantity Logic
        // These run BEFORE totalPoints fallback so they apply even when points = 0
        else if (formType === 'print_only') {
            // [2026-06] Sheets「納期一覧」早見表と整数演算で完全一致。
            // 必要日数 = round(枚数 / ポイントR)。ri は格納 S(2桁) から復元 (computePaperLeadDays)。
            // totalPoints = Liquid 側 variant_points(=S) × qty。
            const sumPoints = totalPoints || 0;
            if (sumPoints <= 0) {
                // ポイント未設定 variant のフォールバック
                requiredDays = 14;
                isApprox = true;
            } else {
                const formEl = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
                const qtyEl = formEl ? formEl.querySelector('[name="quantity"]') : null;
                const qtyForCalc = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;
                const res = computePaperLeadDays([{ qty: qtyForCalc, points: sumPoints }]);
                requiredDays = res ? res.days : 14;
            }
        }
        else if (formType === 'photo_card') {
            let handledByTempLogic = false;
            const form = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
            const qtyInput = form ? form.querySelector('[name="quantity"]') : null;
            const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;

            // [2026-06-12] フォトカードは固定納期(ポイント非依存・セット数=数量)。
            // 4-5=5 / 6-8=7 / 9-10=10 / 11+=要相談(999)。1-3 は従来定義を維持(>=3→5、1-2 はフォールバック)。
            if (qty >= 11) { requiredDays = 999; handledByTempLogic = true; }
            else if (qty >= 9) { requiredDays = 10; handledByTempLogic = true; }
            else if (qty >= 6) { requiredDays = 7; handledByTempLogic = true; }
            else if (qty >= 3) { requiredDays = 5; handledByTempLogic = true; }

            if (!handledByTempLogic) {
                if (totalPoints <= 0) {
                    requiredDays = 14;
                    isApprox = true;
                } else if (totalPoints > this._consultLimit) {
                    requiredDays = 999;
                } else if (totalPoints <= this._limitT1) {
                    requiredDays = Math.ceil((totalPoints / this._capT1) + 1);
                } else {
                    const baseDays = Math.ceil((this._limitT1 / this._capT1) + 1);
                    const excess = totalPoints - this._limitT1;
                    const extraDays = Math.ceil(excess / this._capT2);
                    requiredDays = baseDays + extraDays;
                }
            }
        }
        // 2. Default Fallback (If points = 0, meaning not set up yet) -> Fixed 14 days
        else if (totalPoints <= 0) {
            requiredDays = 14;
            isApprox = true;
        }
        // 3. Standard Calculation
        else {
            if (totalPoints > this._consultLimit) {
                requiredDays = 999;
            } else if (totalPoints <= this._limitT1) {
                requiredDays = Math.ceil((totalPoints / this._capT1) + 1);
            } else {
                const baseDays = Math.ceil((this._limitT1 / this._capT1) + 1);
                const excess = totalPoints - this._limitT1;
                const extraDays = Math.ceil(excess / this._capT2);
                requiredDays = baseDays + extraDays;
            }
        }

        // [NEW] Cart Override Logic for Temporary Rules (Cross-Item Aggregation)
        if (this.dataset.cartItems) {
            try {
                const items = JSON.parse(this.dataset.cartItems);

                // 商品ページモード(自分自身を計算済み)で cart 参照する場合、
                // ループ後の再計算で fresh 値が捨てられないように保存。
                // カート内表示モード(data-form-type 無し)では fresh 計算は
                // formType='' → totalPoints<=0 フォールバックで 14 になる罠があるため
                // ここで使わない (cart items 集計のみを採用)。
                const isProductPageMode = !!(this.dataset.formType && this.dataset.formType.trim());
                const freshSelfDays = isProductPageMode ? requiredDays : 0;

                let totalPrintOnlyA2AndOver = 0;
                let totalPrintOnlyUnderA2 = 0;
                let totalPrintOnly = 0;
                let totalPhotoCard = 0;

                let maxIndividualDays = 0;
                let totalProcessing = 0; // [2026-06-19] 加工系数量の合計 (8点以上=お問い合わせ / Owner)
                let totalFrameOnly = 0;  // [2026-06-19 v2] 額縁単品の点数 (8点以上=お問い合わせ / Owner)
                let hasUnconfiguredItem = false;
                let sumPointsForCalculation = 0;
                // [NEW 2026-06] print_only 専用: Sheets ロジック (S列単純化ポイント × qty 累積)
                let sumPaperPoints = 0;
                let hasPaperItem = false;
                let paperLines = []; // [2026-06] 整数演算用に用紙ラインの {qty, points} を収集

                for (const item of items) {
                    let rawFormType = item.formType || '';
                    if (typeof rawFormType === 'object') {
                        rawFormType = rawFormType.value || rawFormType.type || '';
                    }
                    const itemFormType = String(rawFormType).trim();
                    const qty = parseInt(item.quantity || 0, 10);
                    // [2026-06] 小数の単純化ポイント S (例 A4=1.1 → ×15=16.5) を保持。
                    // parseInt だと 16.5→16 に切り捨てられ ri 復元が狂い納期が早見表とズレる (A4 15枚が3日になる不具合)。
                    const itemPoints = parseFloat(item.points) || 0;
                    const hasDeliveryRule = item.hasDeliveryRule === true;
                    const productId = item.productId ? item.productId.toString() : '';
                    const isSpecial5To7Days = (productId === '7207878197440' || productId === '7207808925888' || itemFormType === 'frame_only');
                    const props = item.properties || {};

                    let tempDays = 0;
                    let handledByTemp = false;

                    if (itemFormType === 'student_discount') {
                        continue;
                    }

                    // [PRIORITY] formType 分岐は isSpecial5To7Days より先。caption_panel 商品が固定 7 日にハイジャックされないようにする。
                    if (itemFormType === 'otameshi_print' || itemFormType === 'caption_panel') {
                        tempDays = 5;
                        handledByTemp = true;
                        // caption_panel: 「出力データ作成を希望する」選択時は +1 営業日
                        if (itemFormType === 'caption_panel') {
                            if (props['出力データ作成オプション'] === '希望する') {
                                tempDays += 1;
                            }
                        }
                        if (itemFormType === 'otameshi_print') {
                            let isAllPaperSet = false;
                            let isSingleSheet = false;
                            let isSingleByType = false; // [2026-06-23] properties[種類] 由来の明示選択(信頼可)。variantTitle のゆるい一致と区別する。
                            // properties[種類] === 'シングル' (etc.) marks single-sheet variants
                            const typeVal = props['種類'];
                            if (typeof typeVal === 'string' && /シングル|単品|1枚|single/i.test(typeVal)) {
                                isSingleSheet = true;
                                isSingleByType = true;
                            }
                            // variantTitle にも全用紙セット/シングルが現れることがある (1-option 構造)
                            const vTitle = typeof item.variantTitle === 'string' ? item.variantTitle : '';
                            if (vTitle.includes('全用紙セット')) isAllPaperSet = true;
                            if (!isSingleSheet && /シングル|単品|1枚|single/i.test(vTitle)) isSingleSheet = true;
                            for (const key in props) {
                                const val = props[key];
                                if (typeof val !== 'string') continue;
                                if (val.includes('全用紙セット') || key.includes('全用紙セット')) isAllPaperSet = true;
                            }
                            if (isSingleSheet) {
                                if (hasDeliveryRule && itemPoints > 0) {
                                    // [2026-06] シングルは A5 紐付け済 → print_only と同じ Sheets 整数演算へ合流
                                    tempDays = 0;
                                    hasPaperItem = true;
                                    sumPaperPoints += itemPoints;
                                    paperLines.push({ qty: qty, points: itemPoints });
                                } else if (isSingleByType) {
                                    // [2026-06-23] 種類=シングル明示選択 → A5 固定で 3営業日。
                                    // PDP rf-dashboard(data-cart-aggregate=true)では pendingItem の variantPoints が解決されず
                                    // hasDeliveryRule=false になるケースがあり、その場合でも単品の正は 3 営業日
                                    // (商品オプション「シングルお試しプリント（3営業日）」)。「シングルが5営業日表示」不具合の根治
                                    // (Owner報告 2026-06-23)。種類プロパティ由来に限定し、下の variantTitle ゆるい一致(セット誤検出)は 5 維持。
                                    tempDays = 3;
                                    handledByTemp = true;
                                } else {
                                    // [2026-06-23] ポイント未紐付け + 種類でなく variantTitle のゆるい一致(セット誤検出の可能性)
                                    // → otameshi 基準 5 営業日を維持(14を出さない。5→14 化け対策)。
                                    tempDays = 5;
                                    handledByTemp = true;
                                }
                            } else if (isAllPaperSet) {
                                // [2026-06-19] 全用紙セット 7 営業日に revert (石黒 QA)
                                tempDays = 7;
                            }
                        }
                    } else if (PROCESSING_FORM_TYPES.has(itemFormType)) {
                        // [2026-06] 加工系は 14 営業日固定
                        handledByTemp = true;
                        tempDays = 14;
                        totalProcessing += qty; // [2026-06-19] 加工点数を累積 (8点以上=受付後確定)
                    } else if (isSpecial5To7Days) {
                        // [2026-06-19 v2] 額縁単品: 5〜7営業日(worst 7・表示「約5〜7」)。8点以上は下で お問い合わせ。
                        tempDays = 7;
                        handledByTemp = true;
                        cartHasSpecial5To7 = true;
                        totalFrameOnly += qty;
                    } else if (itemFormType === 'print_only') {
                        // [2026-06] Sheets 早見表と整数演算で完全一致。paperLines に {qty, points} を収集し
                        // ループ後に computePaperLeadDays で round(Σ(枚数/R)) を厳密計算。
                        handledByTemp = true;
                        tempDays = 0; // print_only 個別の tempDays は使わない (paperLines で集約)
                        hasPaperItem = true;
                        sumPaperPoints += itemPoints; // itemPoints = Liquid 計算済 variant_points × qty
                        paperLines.push({ qty: qty, points: itemPoints });
                        totalPrintOnly += qty;
                        // delivery_rule_ref 未設定の用紙 variant は「負荷不明」として 14日フォールバック対象
                        if (!hasDeliveryRule) {
                            hasUnconfiguredItem = true;
                        }

                        let sizeW = 0, sizeH = 0;
                        const sizeStr = props['サイズ'] || '';
                        const shortStr = props['短辺(mm)'] || '';
                        const longStr = props['長辺(mm)'] || '';

                        if (shortStr && longStr) {
                            sizeW = parseInt(shortStr, 10) || 0;
                            sizeH = parseInt(longStr, 10) || 0;
                        } else if (sizeStr) {
                            const match = sizeStr.match(/\(\s*(\d+)\s*[×xX]\s*(\d+)\s*\)/);
                            if (match) {
                                sizeW = parseInt(match[1], 10);
                                sizeH = parseInt(match[2], 10);
                            } else {
                                const dict = window.STANDARD_SIZE_DICT || {
                                    'A5': [148, 210], 'A4': [210, 297], 'A3': [297, 420], 'A3ノビ': [329, 483], 'A2': [420, 594], 'A2ノビ': [432, 610],
                                    'A1': [594, 841], 'A1ノビ': [610, 914], 'A0': [841, 1189], 'A0ノビ': [914, 1292],
                                    'B5': [182, 257], 'B4': [257, 364], 'B3': [364, 515], 'B2': [515, 728], 'B1': [728, 1030], 'B1ノビ': [790, 1118],
                                    'B0': [1030, 1456], 'B0ノビ': [1118, 1580],
                                    'L': [89, 127], 'ハガキ': [100, 148], '2L': [128, 178], '六切': [203, 254], '四切': [254, 305], 'ワイド四切': [254, 365],
                                    '半切': [356, 432], '全紙': [457, 559], '大全紙': [508, 610], '全倍': [600, 900],
                                    'SM': [158, 227], 'F4': [242, 333], 'F6': [318, 410], 'F8': [380, 455], 'F10': [455, 530], 'F15': [530, 652], 'F20': [606, 727],
                                    '200×200': [200, 200], '300×300': [300, 300], '400×400': [400, 400], '500×500': [500, 500], '600×600': [600, 600],
                                    '800×800': [800, 800], '1000×1000': [1000, 1000], '1000×1500': [1000, 1500], '1000×2000': [1000, 2000], '1000×3000': [1000, 3000]
                                };
                                const keys = Object.keys(dict).sort((a,b) => b.length - a.length);
                                for (const k of keys) {
                                    if (sizeStr.startsWith(k)) {
                                        sizeW = dict[k][0]; sizeH = dict[k][1]; break;
                                    }
                                }
                            }
                        }

                        if (sizeW > 0 && sizeH > 0) {
                            const s = Math.min(sizeW, sizeH);
                            const l = Math.max(sizeW, sizeH);
                            const isUnderA2 = (s < 420 && l < 594);
                            
                            if (!isUnderA2) {
                                totalPrintOnlyA2AndOver += qty;
                            } else {
                                totalPrintOnlyUnderA2 += qty;
                            }
                        }
                    } else if (itemFormType === 'photo_card') {
                        totalPhotoCard += qty;
                        // [2026-06-12] 固定納期(セット数=数量): 4-5=5 / 6-8=7 / 9-10=10 / 11+=要相談。1-3 は従来維持。
                        if (qty >= 11) { tempDays = 999; handledByTemp = true; }
                        else if (qty >= 9) { tempDays = 10; handledByTemp = true; }
                        else if (qty >= 6) { tempDays = 7; handledByTemp = true; }
                        else if (qty >= 3) { tempDays = 5; handledByTemp = true; }
                    } else if (itemPoints <= 0) {
                        tempDays = 14;
                        handledByTemp = true;
                    }

                    if (!handledByTemp) {
                        sumPointsForCalculation += itemPoints; // Accumulate points ONLY for non-temp items
                        if (itemPoints > this._consultLimit) tempDays = 999;
                        else if (itemPoints <= this._limitT1) tempDays = Math.ceil((itemPoints / this._capT1) + 1);
                        else {
                            const baseDays = Math.ceil((this._limitT1 / this._capT1) + 1);
                            const excess = itemPoints - this._limitT1;
                            const extraDays = Math.ceil(excess / this._capT2);
                            tempDays = baseDays + extraDays;
                        }
                    }

                    maxIndividualDays = Math.max(maxIndividualDays, tempDays);

                    if (!hasDeliveryRule && !handledByTemp && itemFormType !== 'photo_card') {
                        hasUnconfiguredItem = true;
                    }
                } // end loop

                // Re-calculate the overall requiredDays using the purely isolated points
                requiredDays = 0;
                if (sumPointsForCalculation > 0) {
                    if (sumPointsForCalculation > this._consultLimit) requiredDays = 999;
                    else if (sumPointsForCalculation <= this._limitT1) requiredDays = Math.ceil((sumPointsForCalculation / this._capT1) + 1);
                    else {
                        const baseDays = Math.ceil((this._limitT1 / this._capT1) + 1);
                        const excess = sumPointsForCalculation - this._limitT1;
                        const extraDays = Math.ceil(excess / this._capT2);
                        requiredDays = baseDays + extraDays;
                    }
                }

                // Update requiredDays to be at least maxIndividualDays
                if (maxIndividualDays > requiredDays) {
                    requiredDays = maxIndividualDays;
                }

                let cartMaxTempDays = 0;
                // print_only の qty バンド (totalPrintOnly / *A2AndOver / *UnderA2) は廃止。
                // 代わりに sumPaperPoints から Sheets ロジックで計算する (下記)。
                // [2026-06-12] 複数フォトカード行の合計セット数でも固定段階を適用(分割行対策)。4-5=5 / 6-8=7 / 9-10=10 / 11+=要相談。
                if (totalPhotoCard >= 11) cartMaxTempDays = Math.max(cartMaxTempDays, 999);
                else if (totalPhotoCard >= 9) cartMaxTempDays = Math.max(cartMaxTempDays, 10);
                else if (totalPhotoCard >= 6) cartMaxTempDays = Math.max(cartMaxTempDays, 7);
                else if (totalPhotoCard >= 3) cartMaxTempDays = Math.max(cartMaxTempDays, 5);

                if (cartMaxTempDays > requiredDays) {
                    requiredDays = cartMaxTempDays;
                }

                // [NEW 2026-06] print_only Sheets ロジックを最後にマージ
                if (hasPaperItem) {
                    let paperDays;
                    const paperRes = computePaperLeadDays(paperLines);
                    if (!paperRes || sumPaperPoints <= 0) {
                        paperDays = 14; // ポイント未設定 variant フォールバック
                        isApprox = true;
                    } else {
                        paperDays = paperRes.days;
                    }
                    if (paperDays > requiredDays) {
                        requiredDays = paperDays;
                    }
                }

                // 商品ページモードで自分自身 (カート未追加) の fresh 計算結果も最後に max に含める
                if (freshSelfDays > requiredDays) {
                    requiredDays = freshSelfDays;
                }

                if (hasUnconfiguredItem) {
                    if (requiredDays < 14) {
                        requiredDays = 14;
                        isApprox = true;
                    }
                }

                // [2026-06-19 v2] 額装加工系 or 額縁単品が 8 点以上 → オーバーフロー (Owner)。
                // [2026-06-22] 額装加工系 or 額縁単品が 8 点以上 → 統一文言オーバーフロー (Owner)。
                if (totalProcessing >= 8 || totalFrameOnly >= 8) {
                    procOverflow = true;
                }
            } catch (e) {
                console.error("LeadTimeCalculator: Failed to parse cart items JSON", e);
            }
        }

        // Min 3 days enforce
        if (requiredDays < 3) requiredDays = 3;

        // [NEW] GW Check Logic
        // Check if current date falls within GW window
        const now = new Date();
        if (this._gwStart && this._gwEnd && this._gwExtraDays > 0) {
            if (now >= this._gwStart && now <= this._gwEnd) {
                requiredDays += this._gwExtraDays;
                console.log(`LeadTime: GW Period Active. Added ${this._gwExtraDays} extra days.`);
            }
        }

        // [NEW] Layout Confirmation +1 Day
        // 例外: caption_panel + 「出力データ作成を希望する」のときは、+1 が既に出力データ作成側で
        // 加算されているため、レイアウト確認の +1 は二重カウントになる → スキップ。
        let wantsLayoutConfirm = false;

        if (this.dataset.cartItems) {
            // Cart mode
            try {
                const items = JSON.parse(this.dataset.cartItems);

                // [2026-06] 出力データ作成希望 item が含まれている場合、レイアウト確認 +1 はカート全体で skip。
                // 理由: 出力データ作成は工程内で「お客様レイアウト確認」を内包しており、
                //       別の完全データ入稿 item のレイアウト確認希望も同タイミングでまとめて連絡可能。
                let hasOutputDataCreation = false;
                for (const item of items) {
                    const props = item.properties || {};
                    let itemFormType = item.formType || '';
                    if (typeof itemFormType === 'object') {
                        itemFormType = itemFormType.value || itemFormType.type || '';
                    }
                    itemFormType = String(itemFormType).trim();
                    if (itemFormType === 'caption_panel' && props['出力データ作成オプション'] === '希望する') {
                        hasOutputDataCreation = true;
                        break;
                    }
                }

                if (!hasOutputDataCreation) {
                    for (const item of items) {
                        const props = item.properties || {};
                        if (props['レイアウト確認'] !== '希望する') continue;
                        let itemFormType = item.formType || '';
                        if (typeof itemFormType === 'object') {
                            itemFormType = itemFormType.value || itemFormType.type || '';
                        }
                        itemFormType = String(itemFormType).trim();
                        wantsLayoutConfirm = true;
                        break;
                    }
                }
            } catch(e) {}
        } else {
            // Product page mode
            const form = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
            if (form) {
                const confirmCheck = form.querySelector('input[name="properties[レイアウト確認]"]:checked');
                if (confirmCheck && confirmCheck.value === '希望する') {
                    // caption_panel + 出力データ作成希望は二重 +1 防止のためスキップ
                    const captionOptRadio = form.querySelector('input[name="properties[出力データ作成オプション]"]:checked');
                    const isCaptionWithOutput = (formType === 'caption_panel') && captionOptRadio && captionOptRadio.value === '希望する';
                    if (!isCaptionWithOutput) {
                        wantsLayoutConfirm = true;
                    }
                }
            }
        }

        if (wantsLayoutConfirm && requiredDays < 999) {
            requiredDays += 1;
        }

        // Status Logic
        let status = 'normal';
        let displayDays = `${requiredDays}営業日出荷`;
        if (isApprox) {
            displayDays = `約${requiredDays}営業日出荷`;
        }

        // [2026-06-22] 額縁単品(額単品)は「7営業日」固定表示 (Owner。旧「約5〜7営業日」から変更)。商品ページは
        // productId/formType 一致、カートは集計で額縁単品が納期を駆動(requiredDays<=7)するとき適用。重量物混在で max>7 は出さない。
        if ((isSpecial5To7Days || cartHasSpecial5To7) && requiredDays <= 7) {
            displayDays = '7営業日出荷';
        }
        let showPrefix = true;
        let isLink = false;
        // [2026-06-22] 8点以上/要相談(>=30日)は顧客向けに「ご注文後に納期をメールでお知らせ」へ統一 (Owner)。
        // 動的 mailto は小さい「今すぐ問い合わせる」ボタンに分離して併記する (額装/額単品で文言は分けない)。
        let consultMail = false;

        if (requiredDays >= 30) {
            status = 'consult';
            consultMail = true;
            showPrefix = false;
        } else if (requiredDays >= 10) {
            status = 'alert';
        }

        // [2026-06-22] 8点以上オーバーフローは他判定に優先 (Owner)。額装加工系/額縁単品とも同一表示。
        if (procOverflow) {
            status = 'consult';
            consultMail = true;
            showPrefix = false;
        }

        // Render
        this.dataset.status = status;

        // Toggle Prefix
        if (this.els.prefix) {
            this.els.prefix.style.display = showPrefix ? 'inline' : 'none';
        }

        if (this.els.days) {
            if (consultMail) {
                // [2026-06-22] 統一文言 +「今すぐ問い合わせる」小ボタン (現行 mailto 立ち上げ機能)。
                const mailto = this.generateMailto();
                this.els.days.innerHTML =
                    `<span class="rf-consult-msg">ご注文後に納期をメールでお知らせ</span>` +
                    `<a href="${mailto}" class="rf-inquiry-mini">事前問合せ ›</a>`;
            } else if (isLink) {
                // Create Link if not exists, or update href
                const mailto = this.generateMailto();
                this.els.days.innerHTML = `<a href="${mailto}" style="color:inherit; text-decoration:underline;">${displayDays}</a>`;
            } else {
                // Reset to text
                this.animateValue(this.els.days, displayDays);
            }
        }

        // --- Generic Date Calculation Feature ---
        if (this.els.date) {
            if (status === 'consult') {
                const isCartMode = !!this.dataset.overridePoints;
                this.els.date.innerText = isCartMode ? '--' : '要相談';
            } else {
                const receptionDate = this.getReceptionDate();
                const shipDate = this.addBusinessDays(receptionDate, requiredDays);

                // Delivery is Ship + 1 to Ship + 4 (Approx)
                const deliveryStart = new Date(shipDate);
                deliveryStart.setDate(deliveryStart.getDate() + 1);

                const deliveryEnd = new Date(shipDate);
                deliveryEnd.setDate(deliveryEnd.getDate() + 4);

                const dateText = `${this.getFormattedDate(deliveryStart)} - ${this.getFormattedDate(deliveryEnd)}`;
                this.animateValue(this.els.date, dateText);
            }
        }

        // --- RF Timer Integration ---
        if (this.els.rfCountdownText && !this._timerInit) {
            this._timerInit = true;
            this.updateCountdown();
            setInterval(() => this.updateCountdown(), 1000);
        }

        // --- RF Timeline Calculation Feature ---
        if (this.els.rfDateReception && this.els.rfDateShipping && this.els.rfDateDelivery) {
            if (status === 'consult') {
                this.els.rfDateShipping.innerHTML = '<span style="font-size:0.8em">要相談</span>';
                this.els.rfDateDelivery.innerHTML = '<span style="font-size:0.8em">要相談</span>';
            } else {
                const receptionDate = this.getReceptionDate();
                this.els.rfDateReception.innerText = this.getFormattedDate(receptionDate).split(' ')[0]; // only MM/DD

                const shipDate = this.addBusinessDays(receptionDate, requiredDays);
                this.els.rfDateShipping.innerText = this.getFormattedDate(shipDate).split(' ')[0];

                const deliveryStart = new Date(shipDate);
                deliveryStart.setDate(deliveryStart.getDate() + 1);
                const deliveryEnd = new Date(shipDate);
                deliveryEnd.setDate(deliveryEnd.getDate() + 4);

                this.els.rfDateDelivery.innerText = `${this.getFormattedDate(deliveryStart).split(' ')[0]} - ${this.getFormattedDate(deliveryEnd).split(' ')[0]}`;

                // Animate Timeline Steps
                if (this.els.rfStepShipping && !this.els.rfStepShipping.classList.contains('active')) {
                    setTimeout(() => {
                        this.els.rfStepShipping.classList.add('active');
                        setTimeout(() => this.els.rfStepDelivery.classList.add('active'), 600);
                    }, 300);
                }
            }
        }

        // --- Cart Attribute Injection ---
        // We only inject if it's the cart context (which has overridePoints data attribute)
        const isCartContext = !!this.dataset.overridePoints;
        if (isCartContext) {
            let attributeString = "";
            
            if (status === 'consult') {
                attributeString = "別途相談";
            } else if (status === 'unavailable') {
                attributeString = "計算不可";
            } else {
                const receptionDate = this.getReceptionDate();
                const shipDateCalc = this.addBusinessDays(receptionDate, requiredDays);

                if (shipDateCalc && !isNaN(shipDateCalc.getTime())) {
                    const y = shipDateCalc.getFullYear();
                    const m = shipDateCalc.getMonth() + 1;
                    const d = shipDateCalc.getDate();
                    const daysArr = ['日', '月', '火', '水', '木', '金', '土'];
                    const dayStr = daysArr[shipDateCalc.getDay()];
                    
                    attributeString = `${y}年${m}月${d}日(${dayStr}) ※${displayDays}`;
                    if (wantsLayoutConfirm) {
                        attributeString += " (レイアウト確認あり)";
                    }
                }
            }
            
            // 1. Fallback: Hidden input for standard form submission
            const cartForm = this.closest('form') || document.querySelector('form[action*="/cart"]');
            if (cartForm) {
                let hiddenInput = cartForm.querySelector('input[name="attributes[出荷予定日]"]');
                if (!hiddenInput) {
                    hiddenInput = document.createElement('input');
                    hiddenInput.type = 'hidden';
                    hiddenInput.name = 'attributes[出荷予定日]';
                    cartForm.appendChild(hiddenInput);
                }
                hiddenInput.value = attributeString;
            }

            // 2. Guaranteed Save: Silent AJAX update (Required for Cart Drawers that intercept checkout)
            // Prevent infinite loops by checking against a global window variable
            if (window._ltCartAttributeSaved !== attributeString) {
                window._ltCartAttributeSaved = attributeString;
                fetch(window.Shopify.routes.root + 'cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        attributes: {
                            '出荷予定日': attributeString
                        }
                    })
                }).then(res => {
                    console.log("LeadTimeCalculator: Cart attribute saved via AJAX ->", attributeString);
                }).catch(err => {
                    console.error("LeadTimeCalculator: Failed to save cart attribute", err);
                });
            }
        }
    }

    animateValue(el, newValue) {
        if (el.innerText !== newValue) {
            el.classList.remove('updated');
            void el.offsetWidth; // Trigger reflow
            el.innerText = newValue;
            el.classList.add('updated');
        }
    }
}

if (!customElements.get('lead-time-calculator')) {
    customElements.define('lead-time-calculator', LeadTimeCalculator);
}

